import { checkPermissionAccess } from "./useCheckPermission";
import { ALWAYS_VISIBLE_NAV_IDS } from "@src/navigation/vertical/navPermissions";

type NavMenuItem = {
  header?: string;
  id?: string;
  children?: NavMenuItem[];
  permission?: string;
  [key: string]: unknown;
};

const isAlwaysVisibleNavItem = (item: NavMenuItem): boolean =>
  Boolean(item.id && ALWAYS_VISIBLE_NAV_IDS.has(item.id));

const isNavLeafVisible = (
  item: NavMenuItem,
  checkPermission: (permissionName?: string) => boolean
): boolean => {
  if (item.children?.length) {
    return item.children.some((child) => isNavLeafVisible(child, checkPermission));
  }

  if (isAlwaysVisibleNavItem(item) || !item.permission) return true;
  return checkPermission(item.permission);
};

const filterNavItem = (
  item: NavMenuItem,
  checkPermission: (permissionName?: string) => boolean
): NavMenuItem | null => {
  if (isAlwaysVisibleNavItem(item)) {
    return item;
  }

  if (item.children?.length) {
    const children = item.children
      .map((child) => filterNavItem(child, checkPermission))
      .filter(Boolean) as NavMenuItem[];

    if (!children.length) return null;
    return { ...item, children };
  }

  if (!item.permission) return item;
  return checkPermission(item.permission) ? item : null;
};

/** Hide sidebar groups/items the user cannot access. */
export const filterNavMenuByPermission = (
  items: NavMenuItem[],
  checkPermission: (permissionName?: string) => boolean
): NavMenuItem[] => {
  const result: NavMenuItem[] = [];

  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];

    if (item.header) {
      let hasVisibleSectionItem = false;

      for (let next = index + 1; next < items.length; next += 1) {
        if (items[next].header) break;
        if (isNavLeafVisible(items[next], checkPermission)) {
          hasVisibleSectionItem = true;
          break;
        }
      }

      if (hasVisibleSectionItem) {
        result.push(item);
      }
      continue;
    }

    const filteredItem = filterNavItem(item, checkPermission);
    if (filteredItem) {
      result.push(filteredItem);
    }
  }

  return result;
};

export const createNavPermissionChecker = (input: {
  userPermissionIds: number[];
  permissionCatalog: Array<{ id: number; name: string }>;
  roleName?: string;
}) => {
  return (permissionName?: string, permissionId?: number) =>
    checkPermissionAccess({
      permissionName,
      permissionId,
      userPermissionIds: input.userPermissionIds,
      permissionCatalog: input.permissionCatalog,
      roleName: input.roleName,
    });
};
